import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../supabaseClient';
import FusionLogo from '../components/FusionLogo';
import { useActionToast } from '../components/ActionToast';
import SupplierFooter from '../components/SupplierFooter';
import SupplierAccountMenu from '../components/SupplierAccountMenu';
import SupplierSlideout from '../components/SupplierSlideout';
import AttachmentViewerModal from '../components/AttachmentViewerModal';
import ComplianceStripe from '../components/compliance/ComplianceStripe';
import ComplianceStateCard from '../components/compliance/ComplianceStateCard';
import DocumentGroup from '../components/compliance/DocumentGroup';
import LibraryCard from '../components/compliance/LibraryCard';
import ComplianceUploadModal from '../components/compliance/ComplianceUploadModal';
import ComplianceReplyModal from '../components/compliance/ComplianceReplyModal';
import ComplianceRespondModal from '../components/compliance/ComplianceRespondModal';
import HostedFormSignPage from './HostedFormSignPage';
import DelegateDocumentModal from '../components/compliance/DelegateDocumentModal';
import { listDelegations, isOpen } from '../lib/delegation';
import PdfSignPage from './PdfSignPage';
import ComplianceAcknowledgeModal from '../components/compliance/ComplianceAcknowledgeModal';
import { useSupplierCompliance } from '../hooks/useSupplierCompliance';
import { DOC_GROUP_ORDER, DOCUMENT_STATES, formatDateBritish } from '../lib/compliance';
import SupplierTour from '../components/SupplierTour';
import { useTour, closeTour, recordTourSeen, SUPPLIER_TOUR_STEPS } from '../lib/supplierTour';

// Per-category library copy for the known shown categories; new categories
// fall back to a generic line.
const LIBRARY_DESC = {
  terms_conditions: 'The contract you signed when joining. View any time.',
  supplier_enhancement: 'Optional – engaging with these documents can unlock Fusion supplier programmes.',
};

// Category names read slowly in ALL CAPS and wrap to two lines
// (doc-library-placeholders brief §5). Admin labels that shout get softened to
// sentence case; labels that already carry their own casing (Terms &
// Conditions, Supplier Enhancement) are left alone.
const toSentenceCase = (label) => {
  const s = (label || '').trim();
  if (!s || /[a-z]/.test(s)) return s;            // already mixed-case → leave it
  const lower = s.toLowerCase();
  return lower.charAt(0).toUpperCase() + lower.slice(1);
};

// A "coming soon" category is a quiet dashed block, NOT a card: no fill, no
// shadow, muted, roughly half the height (brief §1–§3). Grey, never amber – the
// whole point is that nothing is needed. The state lives in the text, not the
// border alone (§8), and there's nothing interactive to focus.
function PlaceholderBlock({ label }) {
  return (
    <div className="border border-dashed border-[#DADCCD] rounded-2xl px-5 py-4">
      <h3 className="font-['Metropolis'] text-[13px] font-bold text-[#7C8071] mb-1">{label}</h3>
      <p className="font-['Metropolis'] text-[11px] text-[#9BA08C]">Coming soon – nothing needed from you</p>
    </div>
  );
}

// Three or more empty categories collapse to ONE strip beneath the populated
// cards (brief §4) – three dashed blocks is a lot of nothing.
function PlaceholderStrip({ names }) {
  return (
    <div className="mt-3.5 border border-dashed border-[#DADCCD] rounded-2xl px-5 py-4">
      <p className="font-['Metropolis'] text-[11px] text-[#9BA08C] leading-relaxed">
        <strong className="text-[#7C8071] font-bold">Coming soon:</strong>{' '}
        {names.join(', ')}. Nothing needed from you – they'll appear here when they're ready.
      </p>
    </div>
  );
}

export default function SupplierCompliance() {
  const navigate = useNavigate();
  const [supplierUser, setSupplierUser] = useState(null);
  const [library, setLibrary] = useState({});
  // Shown-behaviour categories shown in the Document Library (data-driven).
  const [shownCats, setShownCats] = useState([
    { key: 'terms_conditions', label: 'Terms & Conditions' },
    { key: 'supplier_enhancement', label: 'Supplier Enhancement' },
  ]);
  const [loadingShell, setLoadingShell] = useState(true);
  const [slideoutOpen, setSlideoutOpen] = useState(false);
  const [slideoutTab, setSlideoutTab] = useState('profile');
  const { notify, Toast } = useActionToast();
  // Open upload modal – { meta, isReplacement } when set, null when
  // closed. isReplacement drives the Update vs Upload variant copy.
  const [uploadingDoc, setUploadingDoc] = useState(null);
  // Viewer dialog for an already-uploaded document. null when closed;
  // { url, name } when open. Replaces window.open(file_url) so the
  // supplier stays inside /compliance.
  const [viewerDoc, setViewerDoc] = useState(null);
  // Reply modal for Needs Clarification documents. { meta, record }
  // when open. Opens when the supplier clicks Reply (vs Replace,
  // which opens the upload modal).
  const [replyingDoc, setReplyingDoc] = useState(null);
  // Combined reply+replace modal for Rejected / Needs Clarification upload
  // docs. { meta, record } when open. Lets the supplier reply and/or upload a
  // new copy in one submit, and appends to the clarification thread.
  const [respondingDoc, setRespondingDoc] = useState(null);
  // Hosted-form renderer for satisfied_by='form' docs. { meta, record } when
  // open – the supplier fills + signs the form in-app.
  const [formDoc, setFormDoc] = useState(null);
  const [pdfSignDoc, setPdfSignDoc] = useState(null); // { meta, record } for pdf_sign signing
  const [delegateDoc, setDelegateDoc] = useState(null); // { meta } – asking a colleague to do it
  // document_key → the OUTSTANDING delegation on it, so a row can say it's
  // already with someone. Without this you send a document to a colleague and
  // the row looks exactly as it did before – no way to tell you'd sent it, and
  // every reason to send it again.
  const [delegationsByKey, setDelegationsByKey] = useState({});
  // Acknowledge modal for acknowledge-type compliance docs. { meta }
  // when open. One handler covers all states – the modal flips to
  // read-only internally when an ack already exists for the current
  // version (Phase 3 of the acknowledge feature).
  const [acknowledgingDoc, setAcknowledgingDoc] = useState(null);

  const openSlideout = (tab) => { setSlideoutTab(tab); setSlideoutOpen(true); };

  // Onboarding tour – this page hosts the same spotlight overlay as the
  // portal home (shared store in lib/supplierTour), so the cross-page tour
  // can carry onto the compliance steps and back without losing its place.
  const tour = useTour();

  useEffect(() => {
    (async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) { setLoadingShell(false); return; }
      const { data: su } = await supabase
        .from('supplier_users')
        .select('*, suppliers(*)')
        .eq('id', user.id)
        .single();
      setSupplierUser(su);
      setLoadingShell(false);
    })();
  }, []);

  // Library docs – T&Cs and supplier-enhancement reference material the
  // admin has flagged visible_in_portal=true on the Fusion Documents tab.
  // Compliance docs are not in this list (they have their own grouped
  // sections above and are gated by risk tier, not by visibility flag).
  useEffect(() => {
    (async () => {
      // Shown-behaviour categories are the supplier-facing library – read them
      // from document_categories so new ones surface here automatically.
      // Fall back to the original two pre-migration.
      const [{ data: cats }, { data: ecoCat }] = await Promise.all([
        supabase.from('document_categories').select('key, label, sort_order, portal_coming_soon, badge_category_id').eq('behaviour', 'shown').order('sort_order'),
        supabase.from('badge_categories').select('id').eq('slug', 'eco').maybeSingle(),
      ]);
      const ecoId = ecoCat?.id || null;
      const shown = (cats && cats.length)
        ? cats.map(c => ({ ...c, ecoAward: !!ecoId && c.badge_category_id === ecoId }))
        : [{ key: 'terms_conditions', label: 'Terms & Conditions' }, { key: 'supplier_enhancement', label: 'Supplier Enhancement' }];
      setShownCats(shown);
      const { data } = await supabase
        .from('fusion_documents')
        // The full master shape, not just the display fields. A library
        // document can now be COMPLETED from the library (Rob, 2026-08-14:
        // "let's make the library actionable — these recognition documents
        // should not be linked to compliance as they are not required but
        // additional"), and the upload / form / pdf_sign modals need the same
        // meta the required set gets: accepted formats, size cap, the form
        // schema, the source PDF and its field layout, the content hash.
        .select('id, document_name, document_type, description, file_url, satisfied_by, version_value, version_year, created_at, accepted_formats, max_size_mb, supplier_provides_expiry, form_schema, source_pdf_path, field_layout, content_hash, content_richtext, content_file_url, content_file_name, lifecycle, source, compliance_doc_key')
        .in('document_type', shown.map(c => c.key))
        .eq('status', 'active')
        .eq('visible_in_portal', true)
        .order('document_type')
        .order('document_name');
      const grouped = {};
      for (const d of (data || [])) {
        if (!grouped[d.document_type]) grouped[d.document_type] = [];
        grouped[d.document_type].push(d);
      }
      setLibrary(grouped);
    })();
  }, []);

  const supplierId = supplierUser?.supplier_id;
  const riskFlag = supplierUser?.suppliers?.risk_flag || null;
  const compliance = useSupplierCompliance({ supplierId, riskFlag });

  // ---- Library documents the supplier can COMPLETE -------------------------
  //
  // A `shown` category is reference material, and until now the only thing a
  // row could offer was View — which meant a document like the Environmental &
  // Sustainability Questionnaire was published, visible, set to Supplier
  // Uploads, and completely inert. The card above it said "completing the
  // documents here grows your Eco recognition" while offering no way to
  // complete anything.
  //
  // These stay OUT of compliance, deliberately (Rob, 2026-08-14: "these
  // recognition documents should not be linked to compliance as they are not
  // required but additional"). Nothing here can turn a supplier red. They are
  // optional, and completing them feeds the eco award only.
  //
  // The record is fetched separately from the compliance hook, because that
  // hook is scoped to the REQUIRED set by design and widening it would be
  // exactly the coupling this is meant to avoid.
  const [libraryRecords, setLibraryRecords] = useState({});
  const libraryKeys = useMemo(
    () => Object.values(library).flat().map(d => d.compliance_doc_key).filter(Boolean),
    [library],
  );
  const loadLibraryRecords = useCallback(async () => {
    if (!supplierId || libraryKeys.length === 0) { setLibraryRecords({}); return; }
    const [{ data: recs }, { data: acks }] = await Promise.all([
      supabase.from('supplier_onboarding_documents')
        .select('id, document_key, supplier_uploaded_at, supplier_uploaded_file_url, supplier_uploaded_file_name, signed_at, expiry_approved_at, expiry_date, review_decision, review_reason, review_notes, not_required')
        .eq('supplier_id', supplierId).in('document_key', libraryKeys),
      supabase.from('document_acknowledgements')
        .select('id, document_key, acknowledged_at, reviewed_at, review_decision, document_version')
        .eq('supplier_id', supplierId).in('document_key', libraryKeys)
        .order('acknowledged_at', { ascending: false }),
    ]);
    const byKey = {};
    for (const r of recs || []) byKey[r.document_key] = { ...(byKey[r.document_key] || {}), record: r };
    for (const a of acks || []) {
      if (!byKey[a.document_key]?.ack) byKey[a.document_key] = { ...(byKey[a.document_key] || {}), ack: a };
    }
    setLibraryRecords(byKey);
  }, [supplierId, libraryKeys]);
  useEffect(() => { loadLibraryRecords(); }, [loadLibraryRecords]);

  // Master row → the `meta` shape every action modal already speaks. Mirrors
  // useSupplierCompliance's mapping so a library document opens the identical
  // upload / form / signing experience as a required one.
  const libraryMeta = useCallback((d) => ({
    documentId: d.id,
    versionValue: d.version_value,
    contentHash: d.content_hash || null,
    key: d.compliance_doc_key,
    label: d.document_name,
    description: d.description || null,
    acceptedFormats: d.accepted_formats || null,
    maxSizeMb: d.max_size_mb || null,
    supplierProvidesExpiry: d.supplier_provides_expiry === true,
    templateUrl: d.file_url || null,
    satisfiedBy: d.satisfied_by || 'upload',
    formSchema: d.form_schema || null,
    sourcePdfPath: d.source_pdf_path || null,
    fieldLayout: Array.isArray(d.field_layout) ? d.field_layout : null,
    lifecycle: d.lifecycle || null,
    source: d.source || null,
    contentRichtext: d.content_richtext || null,
    contentFileUrl: d.content_file_url || null,
    contentFileName: d.content_file_name || null,
    // Marks this as optional so a modal can soften its language if it wants
    // to. Nothing here blocks award.
    optional: true,
  }), []);

  // What a library row should offer, from what the supplier has already done.
  const libraryAction = useCallback((d) => {
    const key = d.compliance_doc_key;
    if (!key) return { kind: 'none' };
    const { record, ack } = libraryRecords[key] || {};
    const satisfiedBy = d.satisfied_by || 'upload';
    const done = satisfiedBy === 'acknowledge'
      ? !!ack?.acknowledged_at
      : !!(record?.supplier_uploaded_at || record?.signed_at);
    if (done) return { kind: 'done', record, ack };
    if (satisfiedBy === 'form')      return { kind: 'form', record };
    if (satisfiedBy === 'pdf_sign')  return { kind: 'pdf_sign', record };
    if (satisfiedBy === 'acknowledge') return { kind: 'acknowledge', record };
    return { kind: 'upload', record };
  }, [libraryRecords]);

  // ---- How the required-documents section should read today ----------------
  //
  // Tidy-up §1–§3. Three derived facts, all from counts the hook already
  // computes, so nothing here can disagree with the summary card above it.
  const c = compliance.counts || {};
  // Nothing outstanding, expired, expiring, needing clarification or sitting
  // with Fusion — i.e. the supplier has genuinely finished.
  const allClear = !compliance.loading
    && (c.total || 0) > 0
    && !(c.outstanding || c.expired || c.expiringSoon || c.awaitingReview || c.needsClarification);
  // A supplier who has never submitted anything: for them the empty groups ARE
  // the explanation of how this page works, so the teaching copy stays.
  const teachEmptyGroups = !compliance.loading
    && (c.approved || 0) === 0 && (c.awaitingReview || 0) === 0
    && (c.expiringSoon || 0) === 0 && (c.expired || 0) === 0;
  // §2 — urgency-first is right while something needs attention, and wrong when
  // nothing does: it puts two empty sections above the answer. When everything
  // is approved, lead with the approved list.
  const groupOrder = useMemo(
    () => (allClear
      ? [DOCUMENT_STATES.APPROVED, ...DOC_GROUP_ORDER.filter(s => s !== DOCUMENT_STATES.APPROVED)]
      : DOC_GROUP_ORDER),
    [allClear],
  );

  const handleLibraryAction = useCallback((d) => {
    const meta = libraryMeta(d);
    const { kind, record } = libraryAction(d);
    if (kind === 'form')        setFormDoc({ meta, record: record || null });
    else if (kind === 'pdf_sign') setPdfSignDoc({ meta, record: record || null });
    else if (kind === 'acknowledge') setAcknowledgingDoc({ meta });
    else if (kind === 'upload') setUploadingDoc({ meta, isReplacement: false });
  }, [libraryMeta, libraryAction]);

  const supplierInitial = (supplierUser?.suppliers?.supplier_name || '?').trim().charAt(0).toUpperCase();

  // Open the upload modal. Replacement variant (Update label, "previous
  // version stays in effect" success copy) when the current row isn't
  // Outstanding – i.e. the supplier is updating an Approved / Expiring
  // Soon / Expired doc, not uploading one for the first time.
  const handleUpload = useCallback((meta, state) => {
    setUploadingDoc({ meta, isReplacement: state !== DOCUMENT_STATES.OUTSTANDING });
  }, []);

  const handleUploadSuccess = useCallback(() => {
    setUploadingDoc(null);
    compliance.refetch(); loadLibraryRecords();
  }, [compliance, loadLibraryRecords]);

  const handleReply = useCallback((meta, record) => {
    setReplyingDoc({ meta, record });
  }, []);

  const handleReplySuccess = useCallback(() => {
    setReplyingDoc(null);
    compliance.refetch(); loadLibraryRecords();
  }, [compliance, loadLibraryRecords]);

  const handleRespond = useCallback((meta, record) => {
    setRespondingDoc({ meta, record });
  }, []);

  const handleRespondSuccess = useCallback(() => {
    setRespondingDoc(null);
    compliance.refetch(); loadLibraryRecords();
  }, [compliance, loadLibraryRecords]);

  const handleCompleteForm = useCallback((meta, record) => {
    setFormDoc({ meta, record });
  }, []);

  // "Ask someone else" – hand this one document to a colleague better placed
  // to complete or sign it. Only meaningful on documents that are completed
  // in-app; an upload has nothing to delegate.
  const handleDelegate = useCallback((meta) => {
    setDelegateDoc({ meta });
  }, []);

  const loadDelegations = useCallback(async () => {
    if (!supplierId) return;
    try {
      const rows = await listDelegations(supplierId);
      const map = {};
      // Only the outstanding one per document – a withdrawn or completed
      // request isn't something the row should still be reporting.
      for (const d of rows) {
        if (isOpen(d) && !map[d.document_key]) map[d.document_key] = d;
      }
      setDelegationsByKey(map);
    } catch (e) {
      // Non-fatal: the row simply won't show the "sent to" line. The documents
      // themselves must still render.
      console.warn('Could not load delegations:', e.message);
    }
  }, [supplierId]);
  useEffect(() => { loadDelegations(); }, [loadDelegations]);

  const handleSignPdf = useCallback((meta, record) => {
    setPdfSignDoc({ meta, record });
  }, []);

  const handlePdfSignSuccess = useCallback(() => {
    setPdfSignDoc(null);
    compliance.refetch(); loadLibraryRecords();
  }, [compliance, loadLibraryRecords]);

  const handleFormSuccess = useCallback(() => {
    setFormDoc(null);
    compliance.refetch(); loadLibraryRecords();
  }, [compliance, loadLibraryRecords]);

  // Acknowledge handler – opens the read+sign modal. One entry point
  // for all states: the modal flips to readOnly when an ack already
  // exists for the current document_version, so View-signed and
  // first-time Acknowledge share the same handler.
  const handleAcknowledge = useCallback((meta) => {
    setAcknowledgingDoc({ meta });
  }, []);

  const handleAcknowledgeSuccess = useCallback(() => {
    setAcknowledgingDoc(null);
    compliance.refetch(); loadLibraryRecords();
  }, [compliance, loadLibraryRecords]);

  // Persist seen + close on finish/skip. The tour's final step returns to the
  // portal, but a supplier can Skip mid-compliance-leg, so this page records
  // it too (idempotent).
  const handleTourClose = useCallback(() => {
    closeTour();
    recordTourSeen(supplierUser?.supplier_id);
  }, [supplierUser]);

  const handleView = useCallback((meta, record) => {
    if (record?.supplier_uploaded_file_url) {
      setViewerDoc({
        url: record.supplier_uploaded_file_url,
        name: record.supplier_uploaded_file_name || meta.label,
      });
    } else {
      notify({ title: `No file uploaded for “${meta.label}” yet.`, tone: 'info' });
      return;
    }
  }, [notify]);

  if (loadingShell || compliance.loading) {
    return (
      <div className="min-h-screen bg-canvas flex items-center justify-center">
        <p className="font-['Metropolis'] text-[#51534A] tracking-[3px] uppercase text-sm">Loading...</p>
        {/* Keep the tour's dim continuous if it arrived here mid-walkthrough –
            ready=false holds the overlay until the page content exists. */}
        {tour.open && (
          <SupplierTour
            steps={SUPPLIER_TOUR_STEPS}
            currentPath="/portal/compliance"
            ready={false}
            onNavigate={navigate}
            onClose={handleTourClose}
          />
        )}
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-canvas">
      {/* Hide the green "all up to date" stripe here too – the supplier
          is already on the compliance page reading their documents,
          which makes the stripe doubly redundant when everything's
          in order. Amber/red still surface so the prompt to act stays
          visible at the top of the page. */}
      {compliance.state !== 'green' && (
        <ComplianceStripe tone={compliance.stripe.tone} message={compliance.stripe.message} cta={compliance.stripe.cta} />
      )}

      {/* Top utility bar – matches the dashboard. Left-aligned brand
          lockup balanced by the signed-in pill on the right; divider
          + "Supply Pilot" tag drop off below sm and the pill collapses
          to an avatar-only button (handled inside SupplierAccountMenu). */}
      <div className="bg-surface-sidebar border-b border-white/[0.08]">
        <div className="max-w-5xl mx-auto px-4 sm:px-10 h-[76px] sm:h-[90px] flex items-center justify-between gap-4">
          <div className="flex items-center min-w-0">
            <button type="button" onClick={() => navigate('/portal')} aria-label="Fusion 2K Supply Pilot – home" title="Home" className="flex items-center gap-2.5 flex-shrink-0 bg-transparent border-0 p-0 cursor-pointer hover:opacity-80 transition-opacity">
              <FusionLogo icon alt="Fusion" className="h-[38px] w-auto object-contain" />
              <span className="font-['Metropolis'] text-[19px] font-extrabold tracking-[0.14em] text-white leading-none">FUSION</span>
            </button>
            <div className="hidden sm:block w-px h-9 bg-white/15 flex-shrink-0 mx-3.5" />
            <span className="hidden sm:inline-flex items-center whitespace-nowrap font-['Metropolis'] text-[12px] font-bold tracking-[0.16em] uppercase text-sb-text">
              Supply Pilot <span className="text-sb-muted font-semibold">&nbsp;·&nbsp; Partner Portal</span>
            </span>
          </div>
          {supplierUser && (
            <span id="t-settings" className="inline-flex">
              <SupplierAccountMenu
                supplierName={supplierUser.suppliers?.supplier_name}
                supplierEmail={supplierUser.email || supplierUser.suppliers?.main_contact_email}
                initials={supplierInitial}
                complianceState={compliance.state}
                onOpenProfile={() => openSlideout('profile')}
                onOpenHelp={() => openSlideout('help')}
                onChangePassword={() => navigate('/change-password')}
                onReplayTour={() => navigate('/portal', { state: { replayTour: true } })}
                onSignOut={() => { supabase.auth.signOut(); navigate('/'); }}
              />
            </span>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-8 pb-16">
        <button
          type="button"
          onClick={() => navigate('/portal')}
          className="font-['Metropolis'] text-[11px] font-bold uppercase tracking-[0.18em] text-[#51534A] hover:text-fusion-mid-grey inline-flex items-center gap-1.5 mt-4 mb-6"
        >
          <span aria-hidden="true">←</span> Back to dashboard
        </button>

        <div className="mb-8">
          <h1 className="font-['Metropolis'] text-[28px] sm:text-[32px] font-extrabold tracking-tight text-ink-primary mb-2">
            Compliance
          </h1>
          <p className="font-['Metropolis'] text-[13px] text-fusion-mid-grey leading-relaxed max-w-2xl">
            Upload up-to-date copies of your compliance documents below. Click Upload, choose your file, and you'll be asked for the expiry date. Fusion 2K will review and confirm before the document moves to approved. Fusion 2K cannot award new work until all required documents are on file and current.
          </p>
          <button
            type="button"
            onClick={() => navigate('/help/compliance')}
            className="mt-3 inline-flex items-center gap-1.5 font-['Metropolis'] text-[12px] font-semibold text-[#2E8FA1] hover:text-active-green transition-colors bg-transparent border-0 p-0 cursor-pointer">
            How compliance works →
          </button>
        </div>


        {/* An updated version of something they've already signed needs signing
            again (document-versioning §5). Said here, plainly, with the reason
            Fusion gave – a supplier shouldn't have to work out from a changed
            date that they're being asked to re-read a document. */}
        {(compliance.resignRequired || []).length > 0 && (
          <div className="mb-6 rounded-2xl border-[1.5px] border-[#E89A2E]/45 bg-[#E89A2E]/[0.08] p-5">
            <div className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.2em] text-[#7A4E10] mb-2">
              {compliance.resignRequired.length === 1 ? 'A document needs signing again' : `${compliance.resignRequired.length} documents need signing again`}
            </div>
            <ul className="flex flex-col gap-2.5">
              {compliance.resignRequired.map(item => (
                <li key={item.key} className="font-['Metropolis'] text-[13px] text-ink leading-snug">
                  <strong className="font-extrabold">{item.label}</strong>
                  {item.state === 'resign_due' && item.dueDate && <> – sign again by {formatDateBritish(item.dueDate)}. You're compliant until then.</>}
                  {item.state === 'lapsed' && item.dueDate && <> – the deadline was {formatDateBritish(item.dueDate)}, so this is now outstanding.</>}
                  {item.state === 'invalid_now' && <> – needs signing now.</>}
                  {item.reason && (
                    <div className="text-[12px] text-[#51534A] mt-0.5">What changed: {item.reason}</div>
                  )}
                </li>
              ))}
            </ul>
          </div>
        )}

        <div id="t-compliance-summary">
          <ComplianceStateCard state={compliance.state} counts={compliance.counts} />
        </div>

        <div id="t-compliance-docs" className="mb-10">
          <div className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.25em] text-fusion-forest mb-2">
            Required Documents
          </div>
          {/* §3 — say plainly when there is nothing to do. This page is mostly
              read by suppliers who are already compliant, and telling them that
              in one line is more use than a description of a checklist they
              have finished. Factual, not congratulatory. */}
          {allClear ? (
            <p className="font-['Metropolis'] text-[13px] font-bold text-fusion-forest leading-snug mb-4 flex items-center gap-2">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true"><polyline points="20 6 9 17 4 12" /></svg>
              All documents current – nothing needed from you right now.
            </p>
          ) : (
            <p className="font-['Metropolis'] text-[12px] text-[#51534A] leading-snug mb-4">
              Documents needed before Fusion 2K can award new work. Grouped by current state – what needs your attention first is at the top.
            </p>
          )}
          {groupOrder.map(state => (
            <DocumentGroup
              key={state}
              state={state}
              teachEmpty={teachEmptyGroups}
              entries={compliance.groups.get(state) || []}
              onUpload={handleUpload}
              onView={handleView}
              onReply={handleReply}
              onRespond={handleRespond}
              onAcknowledge={handleAcknowledge}
              onCompleteForm={handleCompleteForm}
              onSignPdf={handleSignPdf}
              onDelegate={handleDelegate}
              delegationsByKey={delegationsByKey}
              onOpenHistory={(key) => navigate(`/portal/compliance/${encodeURIComponent(key)}/history`)}
              messagesByKey={compliance.messagesByKey}
            />
          ))}
        </div>

        {/* A category appears when it has a portal-visible document, OR when
            it's empty but flagged "Coming soon" in Manage Categories. Empty +
            not-flagged categories stay hidden. The whole section drops out when
            nothing is visible. */}
        {(() => {
          const populated = shownCats.filter(cat => (library[cat.key] || []).length > 0);
          const placeholders = shownCats.filter(cat =>
            (library[cat.key] || []).length === 0 && cat.portal_coming_soon
          );
          if (populated.length === 0 && placeholders.length === 0) return null;
          // 3+ empties → one strip below the cards; 1–2 → a quiet block each,
          // in the grid. Either way they render AFTER the populated cards,
          // never interleaved (brief §4).
          const groupStrip = placeholders.length >= 3;
          return (
            <div>
              <div className="font-['Metropolis'] text-[11px] font-extrabold uppercase tracking-[0.25em] text-fusion-forest mb-2">
                Document Library
              </div>
              <p className="font-['Metropolis'] text-[12px] text-[#51534A] leading-snug mb-4">
                Reference documents from Fusion 2K – your terms and any optional supplier material.
              </p>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3.5">
                {populated.map(cat => (
                  <LibraryCard
                    key={cat.key}
                    label={toSentenceCase(cat.label)}
                    description={LIBRARY_DESC[cat.key] || 'Reference documents from Fusion 2K.'}
                    documents={library[cat.key] || []}
                    ecoAward={!!cat.ecoAward}
                    actionFor={libraryAction}
                    onAction={handleLibraryAction}
                  />
                ))}
                {!groupStrip && placeholders.map(cat => (
                  <PlaceholderBlock key={cat.key} label={toSentenceCase(cat.label)} />
                ))}
              </div>
              {groupStrip && (
                <PlaceholderStrip names={placeholders.map(cat => toSentenceCase(cat.label))} />
              )}
            </div>
          );
        })()}
      </div>

      {/* Full-bleed band – no container wrapper; the footer aligns its own
          content to the page container internally. */}
      <SupplierFooter
        onBrowseHelp={() => openSlideout('help')}
        containerClassName="max-w-5xl px-8" />

      <Toast />

      <SupplierSlideout
        open={slideoutOpen}
        onClose={() => setSlideoutOpen(false)}
        initialTab={slideoutTab}
        supplier={supplierUser?.suppliers}
        supplierUser={supplierUser}
      />

      {uploadingDoc && supplierId && (
        <ComplianceUploadModal
          document={uploadingDoc.meta}
          supplierId={supplierId}
          isReplacement={uploadingDoc.isReplacement}
          onSuccess={handleUploadSuccess}
          onClose={() => setUploadingDoc(null)}
        />
      )}

      {replyingDoc && supplierId && (
        <ComplianceReplyModal
          document={replyingDoc.meta}
          record={replyingDoc.record}
          supplierId={supplierId}
          onSuccess={handleReplySuccess}
          onClose={() => setReplyingDoc(null)}
        />
      )}

      {respondingDoc && supplierId && (
        <ComplianceRespondModal
          document={respondingDoc.meta}
          record={respondingDoc.record}
          supplierId={supplierId}
          onSuccess={handleRespondSuccess}
          onClose={() => setRespondingDoc(null)}
        />
      )}

      {/* Hosted documents sign on a FULL PAGE, never in a dialog
          (long-documents §6). One renderer serves a one-paragraph
          acknowledgement and a ten-page code of conduct, so there's no second
          layout to keep in step – and a document longer than a screen is no
          longer trapped in a modal body. */}
      {formDoc && supplierId && (
        <HostedFormSignPage
          document={formDoc.meta}
          supplier={supplierUser?.suppliers}
          supplierId={supplierId}
          signer={{ name: supplierUser?.full_name, email: supplierUser?.email }}
          onSuccess={handleFormSuccess}
          onClose={() => setFormDoc(null)}
        />
      )}

      {delegateDoc && supplierId && (
        <DelegateDocumentModal
          document={delegateDoc.meta}
          supplierId={supplierId}
          supplierName={supplierUser?.suppliers?.supplier_name}
          invitedByUserId={supplierUser?.id}
          invitedByName={supplierUser?.full_name}
          onClose={() => setDelegateDoc(null)}
          onChanged={() => { compliance.refetch(); loadLibraryRecords(); loadDelegations(); }}
        />
      )}

      {pdfSignDoc && supplierId && (
        <PdfSignPage
          document={pdfSignDoc.meta}
          supplier={supplierUser?.suppliers}
          supplierId={supplierId}
          supplierUserId={supplierUser?.id}
          signer={{ name: supplierUser?.full_name, email: supplierUser?.email }}
          onSuccess={handlePdfSignSuccess}
          onExit={() => setPdfSignDoc(null)}
        />
      )}

      {/* Acknowledge modal – read + sign for compliance docs flagged
          satisfied_by='acknowledge'. The hook's `acknowledgements`
          array is already filtered to this supplier; we look up the
          most-recent ack for this doc's key to drive the modal's
          read-only mode. */}
      {acknowledgingDoc && supplierUser && (() => {
        const existingAck = (compliance.acknowledgements || []).find(a => a.document_key === acknowledgingDoc.meta.key);
        return (
          <ComplianceAcknowledgeModal
            open
            meta={acknowledgingDoc.meta}
            supplierUser={supplierUser}
            existingAck={existingAck}
            onSubmitted={handleAcknowledgeSuccess}
            onClose={() => setAcknowledgingDoc(null)}
          />
        );
      })()}

      {/* Shared attachment viewer – image / document preview with the
          improved sizing (small images centred + bordered, large images
          get more room). */}
      <AttachmentViewerModal doc={viewerDoc} onClose={() => setViewerDoc(null)} />

      {/* Onboarding tour – the compliance leg of the cross-page walkthrough.
          The page is loaded by here, so ready=true: anchors exist and the
          spotlight settles on them (or the tour carries back to the portal
          for its remaining steps). */}
      {tour.open && (
        <SupplierTour
          steps={SUPPLIER_TOUR_STEPS}
          currentPath="/portal/compliance"
          ready
          onNavigate={navigate}
          onClose={handleTourClose}
        />
      )}
    </div>
  );
}
